import {useQuery} from "react-query";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {
    ReservationService
} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    getStayEndDate,
    getStayStartDate,
    pickPreferredRestaurantId,
    pickStayForDate,
    ProfileStayContext
} from "./profileStay.util";

interface Params {
    profileId?: string
    /** Restaurant sector - scopes the lookup of the sittings the guest already has. */
    restaurantSectorId?: string
    /** Every restaurant of that sector; the guest's own restaurant is picked from these. */
    restaurantIds: string[]
    /** Day the reservation is being created for - decides which stay is the relevant one. */
    referenceDate?: Date
    enabled?: boolean
}

/**
 * Resolves the stay context of a single guest: their own accommodation reservation (check-in /
 * check-out / room guest counts) plus the restaurant they already eat at.
 *
 * This is deliberately per-profile rather than per reservation group - a group split across
 * several rooms has several check-out dates, and the dialog must show the one belonging to the
 * guest the user selected.
 */
export const useProfileStayContext = ({
                                          profileId,
                                          restaurantSectorId,
                                          restaurantIds,
                                          referenceDate,
                                          enabled = true,
                                      }: Params) => {
    const referenceDay = referenceDate ? referenceDate.toDateString() : undefined

    const query = useQuery<ProfileStayContext | undefined>(
        ["profileStayContext", profileId, restaurantSectorId, referenceDay],
        async () => {
            if (!profileId) return undefined

            const profile = await ProfileService.getProfileById(profileId)
            const stay = pickStayForDate(profile, referenceDate)

            const accommodationReservation = stay?.reservationId
                ? await ReservationService.getReservationById(stay.reservationId).catch(() => undefined)
                : undefined

            const checkInDate = getStayStartDate(accommodationReservation)
            const checkOutDate = getStayEndDate(accommodationReservation)

            // The guest's existing sittings tell us which restaurant they belong to - guests of
            // one group are regularly split across restaurants.
            const restaurantReservations = restaurantSectorId && restaurantIds.length
                ? await ReservationService.getAllReservations({
                    profileIds: [profileId],
                    sectorIds: [restaurantSectorId],
                    status: [
                        ReservationStatus.CONFIRMED,
                        ReservationStatus.ACTIVE,
                        ReservationStatus.COMPLETED,
                    ],
                    ...(checkInDate && checkOutDate
                        ? {overlaps: {start: checkInDate, end: checkOutDate}}
                        : {}),
                    limit: {offset: 0, limit: 100},
                }).then(page => page.content ?? []).catch(() => [])
                : []

            return {
                profileId,
                accommodationReservation,
                checkInDate,
                checkOutDate,
                guestCounts: accommodationReservation?.guestCounts,
                restaurantId: pickPreferredRestaurantId(restaurantReservations, restaurantIds),
                notes: accommodationReservation?.notes,
                tableReservationEvent: accommodationReservation?.tableReservationEvent,
            }
        },
        {
            enabled: enabled && Boolean(profileId),
            // The stay of a guest does not change while a dialog is open - avoid refetching it
            // on every window focus, which would re-run the recalculation mid-edit.
            refetchOnWindowFocus: false,
            staleTime: 60_000,
        }
    )

    return {
        profileStayContext: query.data,
        loadingProfileStayContext: query.isFetching,
    }
}
